import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Home, BookOpen, History, Phone, LogOut, Send, Monitor, KeyRound, X, Menu, Shield, HelpCircle, Heart, TrendingUp, Users, ChevronDown, MoreHorizontal, QrCode, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const allFullNavItems = [
  { path: '/', icon: Home, label: 'Tableau de bord', perm: 'tableau_de_bord' },
  { path: '/scenarios', icon: BookOpen, label: 'Scénarios', perm: 'scenarios' },
  { path: '/random-call', icon: Phone, label: 'Appels 911', perm: 'appels_911' },
  { path: '/assigned-calls', icon: Send, label: 'Appels Assignés', perm: 'appels_assignes' },
  { path: '/rao', icon: Monitor, label: 'RAO', perm: 'rao' },
  { path: '/health', icon: Heart, label: 'Santé', perm: 'ambulance' },
  { path: '/equipe', icon: Users, label: 'Équipe', perm: null },
  { path: '/rendez-vous', icon: CalendarDays, label: 'Rendez-vous', perm: null },
  { path: '/history', icon: History, label: 'Historique', perm: 'historique' },
  { path: '/progress', icon: TrendingUp, label: 'Mes Progrès', perm: 'historique' },
  { path: '/help', icon: HelpCircle, label: 'Aide', perm: null },
  { path: '/qr', icon: QrCode, label: 'QR', perm: 'admin' },
  { path: '/rao-admin', icon: Shield, label: 'Admin', perm: 'admin' },
];

// Secondary items grouped under the "Plus" dropdown on desktop
const MORE_PATHS = ['/assigned-calls', '/rendez-vous', '/history', '/progress', '/help'];

const raoNavItems = [
  { path: '/rao-mode', icon: Monitor, label: 'Formation RAO', perm: null },
];

const Navigation = ({ mode = 'full', onLogout, studentName, studentEmail, permissions = {} }) => {
  const location = useLocation();
  const isRAO = mode === 'rao';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = isRAO
    ? raoNavItems
    : allFullNavItems.filter(item => item.perm === null || permissions[item.perm]);
  const primaryItems = navItems.filter(item => !MORE_PATHS.includes(item.path));
  const moreItems = navItems.filter(item => MORE_PATHS.includes(item.path));
  const [moreOpen, setMoreOpen] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/rao/auth/change-password`, {
        email: studentEmail,
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Mot de passe modifié avec succès');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du changement');
    } finally {
      setChangingPassword(false);
    }
  };

  const activeBg = isRAO ? '#24B219' : '#002768';

  return (
    <>
      <nav data-testid="main-navigation" className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Hamburger (mobile) + Logo */}
            <div className="flex items-center space-x-3">
              {/* Hamburger - mobile only */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 text-gray-600"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>

              <div className="w-10 h-10 rounded-full flex items-center justify-center"
                   style={{ background: isRAO
                     ? 'linear-gradient(135deg, #24B219, #82F486)'
                     : 'linear-gradient(135deg, #002768, #2E6CF5)'
                   }}>
                {isRAO ? <Monitor className="w-5 h-5 text-white" /> : <Phone className="w-5 h-5 text-white" />}
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold whitespace-nowrap" style={{ color: '#002768' }}>
                  {isRAO ? 'Formation RAO' : 'Simulateur 911'}
                </h1>
                <p className="text-xs text-gray-400 italic cm-slogan">
                  Avance à ta façon
                </p>
              </div>
            </div>

            {/* Center: Desktop nav links (icons-only between lg and xl, full labels at xl+) */}
            <div className="hidden lg:flex items-center space-x-0.5">
              {primaryItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={item.label}
                    data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center space-x-1.5 px-2.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'text-white'
                        : 'text-gray-600 hover:bg-white/50'
                    }`}
                    style={isActive ? { backgroundColor: activeBg } : {}}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="hidden xl:inline">{item.label}</span>
                  </Link>
                );
              })}
              {moreItems.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setMoreOpen(o => !o)}
                    title="Plus d'options"
                    data-testid="nav-more-btn"
                    className={`flex items-center space-x-1.5 px-2.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      moreItems.some(i => location.pathname === i.path)
                        ? 'text-white'
                        : 'text-gray-600 hover:bg-white/50'
                    }`}
                    style={moreItems.some(i => location.pathname === i.path) ? { backgroundColor: activeBg } : {}}
                  >
                    <MoreHorizontal className="w-4 h-4 flex-shrink-0" />
                    <span className="hidden xl:inline">Plus</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {moreOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} data-testid="nav-more-overlay" />
                      <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50" data-testid="nav-more-dropdown">
                        {moreItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              data-testid={`nav-more-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              onClick={() => setMoreOpen(false)}
                              className={`flex items-center space-x-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                                isActive ? 'text-white' : 'text-gray-700 hover:bg-gray-50'
                              }`}
                              style={isActive ? { backgroundColor: activeBg } : {}}
                            >
                              <Icon className="w-4 h-4" />
                              <span>{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right: User actions */}
            {onLogout && (
              <div className="flex items-center gap-2">
                {studentName && (
                  <span className="text-sm font-medium hidden sm:inline" style={{ color: '#002768' }}>{studentName}</span>
                )}
                {studentEmail && (
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="p-2 text-gray-500 hover:bg-white/50 rounded-lg"
                    title="Changer le mot de passe"
                    data-testid="change-password-button"
                  >
                    <KeyRound className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={onLogout}
                  className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:bg-white/50 rounded-lg font-medium"
                  data-testid="logout-button"
                  title="Déconnexion"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm">Déconnexion</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile side panel overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black/40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Mobile side panel */}
      <div
        className={`fixed top-0 left-0 h-full w-72 z-[56] lg:hidden transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)' }}
        data-testid="mobile-side-panel"
      >
        {/* Panel header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
                 style={{ background: isRAO
                   ? 'linear-gradient(135deg, #24B219, #82F486)'
                   : 'linear-gradient(135deg, #002768, #2E6CF5)'
                 }}>
              {isRAO ? <Monitor className="w-5 h-5 text-white" /> : <Phone className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: '#002768' }}>
                {isRAO ? 'Formation RAO' : 'Simulateur 911'}
              </h2>
              <p className="text-xs text-gray-400 italic">Avance à ta façon</p>
            </div>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100"
            data-testid="mobile-menu-close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Student name */}
        {studentName && (
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold" style={{ color: '#002768' }}>{studentName}</p>
            {studentEmail && <p className="text-xs text-gray-400">{studentEmail}</p>}
          </div>
        )}

        {/* Nav links - scrollable area with padding for bottom actions */}
        <div className="py-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center space-x-3 px-5 py-3.5 font-medium transition-colors ${
                  isActive
                    ? 'text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                style={isActive ? { backgroundColor: activeBg } : {}}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Bottom actions */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-4 space-y-2">
          {studentEmail && (
            <button
              onClick={() => { setMobileMenuOpen(false); setShowPasswordModal(true); }}
              className="flex items-center space-x-3 w-full px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg font-medium"
              data-testid="mobile-change-password"
            >
              <KeyRound className="w-5 h-5" />
              <span className="text-sm">Changer le mot de passe</span>
            </button>
          )}
          {onLogout && (
            <button
              onClick={() => { setMobileMenuOpen(false); onLogout(); }}
              className="flex items-center space-x-3 w-full px-3 py-2.5 text-rose-600 hover:bg-rose-50 rounded-lg font-medium"
              data-testid="mobile-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Déconnexion</span>
            </button>
          )}
        </div>
      </div>

      {/* Password change modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: '#002768' }}>Changer le mot de passe</h3>
              <button onClick={() => setShowPasswordModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe actuel</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                  style={{ '--tw-ring-color': '#2E6CF5' }}
                  required
                  data-testid="current-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                  style={{ '--tw-ring-color': '#2E6CF5' }}
                  required
                  minLength={4}
                  data-testid="new-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                  style={{ '--tw-ring-color': '#2E6CF5' }}
                  required
                  minLength={4}
                  data-testid="confirm-password"
                />
              </div>
              <button
                type="submit"
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="w-full py-2.5 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-all"
                style={{ backgroundColor: '#002768' }}
                data-testid="submit-password-change"
              >
                {changingPassword ? 'Modification...' : 'Modifier le mot de passe'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
